import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import AvatarGroupStacked from '../shared/AvatarGroupStacked';
import InsightsChart from '../shared/InsightsChart';
import InsightsDashboardFilters from './InsightsDashboardFilters';
import { extractFilterDefinitions, applyFilters } from './insightsFilterUtils';

function formatRelativeDate(dateString, t) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t('Today');
  if (diffDays === 1) return t('1 day ago');
  if (diffDays < 30) return t('{{count}} days ago', { count: diffDays });
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return t('1 month ago');
  if (diffMonths < 12) return t('{{count}} months ago', { count: diffMonths });
  const diffYears = Math.floor(diffDays / 365);
  if (diffYears === 1) return t('1 year ago');
  return t('{{count}} years ago', { count: diffYears });
}

/**
 * InsightsDashboardView
 *
 * Props from Redux (via container):
 *   currentDashboard        - dashboard detail object: { dashboard_id, title, description,
 *                             created_at, users[], charts[] }
 *                             charts[] contains metadata only (no data field yet)
 *   loading                 - global insights loading flag
 *   fetchDashboardDetail(id) - dispatched thunk → loads dashboard into Redux
 *   fetchChartData(chartId)  - dispatched thunk → returns chart with data (Promise)
 *
 * Chart data loading strategy:
 *   Dashboard detail endpoint returns charts[] with metadata only (no query data).
 *   We fetch each chart's data in parallel via fetchChartData thunks (which return
 *   the resolved data), then store results in local component state (chartsData map).
 *   This avoids overwriting a single Redux field with concurrent requests.
 */
function InsightsDashboardView(props) {
  const {
    currentDashboard,
    loading = false,
    fetchDashboardDetail,
    fetchChartData,
    t,
  } = props;

  const { id } = useParams();
  const navigate = useNavigate();

  // chartsData: { [chartId]: chartObjectWithData }
  const [chartsData, setChartsData] = useState({});
  const [chartsLoading, setChartsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  // Track the last loaded dashboard id to avoid duplicate fetches
  const loadedDashboardId = useRef(null);

  useEffect(() => {
    if (id) {
      fetchDashboardDetail(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      !currentDashboard ||
      !currentDashboard.charts ||
      currentDashboard.charts.length === 0
    ) {
      return;
    }

    // Avoid re-fetching if the same dashboard is already loaded
    if (loadedDashboardId.current === currentDashboard.dashboard_id) {
      return;
    }
    loadedDashboardId.current = currentDashboard.dashboard_id;

    const chartList = currentDashboard.charts;
    setChartsLoading(true);
    setChartsData({});
    setActiveFilters({});

    // fetchChartData thunk returns the resolved chart data (or null on error)
    const chartPromises = chartList.map(chart =>
      fetchChartData(chart.chart_id).then(data => ({
        chartId: chart.chart_id,
        data,
      }))
    );

    Promise.all(chartPromises).then(results => {
      const newChartsData = {};
      results.forEach(({ chartId, data }) => {
        if (data) {
          newChartsData[chartId] = data;
        }
      });
      setChartsData(newChartsData);
      setChartsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDashboard]);

  const filterDefinitions = useMemo(
    () => extractFilterDefinitions(chartsData),
    [chartsData]
  );

  const filteredChartsData = useMemo(
    () => applyFilters(chartsData, activeFilters),
    [chartsData, activeFilters]
  );

  const handleFilterChange = useCallback((key, values) => {
    setActiveFilters(prev => ({ ...prev, [key]: values }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  if (loading && !currentDashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && !currentDashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t('Dashboard not found.')}
        </Typography>
      </Box>
    );
  }

  if (!currentDashboard) return null;

  const charts = currentDashboard.charts || [];

  return (
    <Box sx={{ p: 2 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/insights')}>
          Insights
        </Link>
        <Typography variant="body2" color="text.primary">
          {currentDashboard.title}
        </Typography>
      </Breadcrumbs>

      {/* Dashboard header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {currentDashboard.title}
          </Typography>
          <AvatarGroupStacked users={currentDashboard.users || []} maxVisible={4} />
        </Box>
        {currentDashboard.description && (
          <Typography variant="body2" color="text.secondary">
            {currentDashboard.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {formatRelativeDate(currentDashboard.created_at, t)}
        </Typography>
      </Box>

      {/* Filters */}
      {filterDefinitions.length > 0 && (
        <InsightsDashboardFilters
          filterDefinitions={filterDefinitions}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearFilters}
        />
      )}

      {/* Charts grid */}
      {chartsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {charts.map(chartMeta => {
            const chartWithData = filteredChartsData[chartMeta.chart_id];
            return (
              <Grid
                item
                xs={12}
                md={chartMeta.grid_cols || 12}
                key={chartMeta.chart_id}>
                <InsightsChart chart={chartWithData || null} loading={false} />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}

export default withTranslation()(InsightsDashboardView);
